import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  DollarSign, TrendingUp, TrendingDown, BarChart3, 
  AlertCircle, Loader2, RefreshCw, Info, Settings
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useProject } from '../contexts/ProjectContext';
import { Action } from '../lib/auth';
import api from '../lib/api';

const EVMPage = () => {
  const { activeSchedule, can } = useProject();

  // State
  const [evmData, setEvmData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Setup dialog state
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [defaultBac, setDefaultBac] = useState(1000);
  
  // Load EVM data
  const loadEvmData = useCallback(async () => {
    if (!activeSchedule?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/evm/schedule/${activeSchedule.id}`);
      setEvmData(response.data);
    } catch (err) {
      console.error('Failed to load EVM data:', err);
      setError('Failed to load EVM data');
    } finally {
      setLoading(false);
    }
  }, [activeSchedule?.id]);
  
  useEffect(() => {
    loadEvmData();
  }, [loadEvmData]);
  
  // Setup default BAC
  const handleSetupBac = async () => {
    if (!activeSchedule?.id) return;
    
    setSettingUp(true);
    
    try {
      await api.post(`/evm/schedule/${activeSchedule.id}/bulk-bac?default_bac=${defaultBac}`);
      setSetupDialogOpen(false);
      loadEvmData();
    } catch (err) {
      console.error('Failed to setup BAC:', err);
      setError('Failed to setup BAC');
    } finally {
      setSettingUp(false);
    }
  };
  
  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  // Format percent
  const formatPercent = (value) => {
    if (value === null || value === undefined) return '—';
    return `${(value * 100).toFixed(1)}%`;
  };
  
  // Format index
  const formatIndex = (value) => {
    if (value === null || value === undefined) return '—';
    return value.toFixed(2);
  };
  
  // Get CPI/SPI status color key
  const getIndexStatus = (value) => {
    if (value === null || value === undefined) return 'slate';
    if (value >= 1) return 'green';
    if (value >= 0.9) return 'amber';
    return 'red';
  };

  // Static Tailwind class lookup — dynamic interpolation like `border-${var}-200`
  // doesn't work because Tailwind purges unseen class names at build time.
  const indexStyles = {
    green: { card: 'border-green-200 bg-green-50/30', text: 'text-green-700', icon: 'text-green-500' },
    amber: { card: 'border-amber-200 bg-amber-50/30', text: 'text-amber-700', icon: 'text-amber-500' },
    red:   { card: 'border-red-200 bg-red-50/30',     text: 'text-red-700',   icon: 'text-red-500' },
    slate: { card: 'border-slate-200 bg-slate-50/30',  text: 'text-slate-700', icon: 'text-slate-500' },
  };
  
  // No schedule selected
  if (!activeSchedule) {
    return (
      <div className="flex flex-col items-center justify-center h-64" data-testid="evm-page">
        <BarChart3 className="h-12 w-12 text-slate-300 mb-4" />
        <h2 className="text-lg font-medium text-slate-900 mb-2">No Schedule Selected</h2>
        <p className="text-sm text-slate-500">Select a project and schedule to view EVM</p>
      </div>
    );
  }

  const summary = evmData?.summary;

  return (
    <div className="space-y-6" data-testid="evm-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Earned Value Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Performance metrics for {activeSchedule.name}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {can(Action.MANAGE_SCHEDULES) && (
            <Button 
              variant="outline"
              size="sm"
              className="h-9 gap-2"
              onClick={() => setSetupDialogOpen(true)}
            >
              <Settings className="h-4 w-4" />
              Setup BAC
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={loadEvmData}
            disabled={loading}
            className="h-9 gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
          <Button variant="ghost" size="sm" className="ml-auto h-6" onClick={() => setError(null)}>
            Dismiss
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : summary ? (
        <>
          {/* Performance Indexes */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* CPI */}
            <Card className={indexStyles[getIndexStatus(summary.cpi)].card}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-slate-500 uppercase">CPI</p>
                  <DollarSign className={`h-4 w-4 ${indexStyles[getIndexStatus(summary.cpi)].icon}`} />
                </div>
                <p className={`text-2xl font-bold ${indexStyles[getIndexStatus(summary.cpi)].text}`}>
                  {formatIndex(summary.cpi)}
                </p>
                <p className="text-xs text-slate-500 mt-1">Cost Performance Index</p>
              </CardContent>
            </Card>

            {/* SPI */}
            <Card className={indexStyles[getIndexStatus(summary.spi)].card}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-slate-500 uppercase">SPI</p>
                  <TrendingUp className={`h-4 w-4 ${indexStyles[getIndexStatus(summary.spi)].icon}`} />
                </div>
                <p className={`text-2xl font-bold ${indexStyles[getIndexStatus(summary.spi)].text}`}>
                  {formatIndex(summary.spi)}
                </p>
                <p className="text-xs text-slate-500 mt-1">Schedule Performance Index</p>
              </CardContent>
            </Card>
            
            {/* CV */}
            <Card className="border-slate-200">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-slate-500 uppercase">CV</p>
                  {summary.cv >= 0 ? (
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500" />
                  )}
                </div>
                <p className={`text-2xl font-bold ${summary.cv >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(summary.cv)}
                </p>
                <p className="text-xs text-slate-500 mt-1">Cost Variance</p>
              </CardContent>
            </Card>
            
            {/* SV */}
            <Card className="border-slate-200">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-slate-500 uppercase">SV</p>
                  {summary.sv >= 0 ? (
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500" />
                  )}
                </div>
                <p className={`text-2xl font-bold ${summary.sv >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(summary.sv)}
                </p>
                <p className="text-xs text-slate-500 mt-1">Schedule Variance</p>
              </CardContent>
            </Card>
          </div>

          {/* Budget Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900">
                  Budget Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-600">BAC (Budget at Completion)</span>
                  <span className="text-sm font-medium text-slate-900">{formatCurrency(summary.total_bac)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-600">PV (Planned Value)</span>
                  <span className="text-sm font-medium text-slate-900">{formatCurrency(summary.total_pv)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-600">EV (Earned Value)</span>
                  <span className="text-sm font-medium text-slate-900">{formatCurrency(summary.total_ev)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-600">AC (Actual Cost)</span>
                  <span className="text-sm font-medium text-slate-900">{formatCurrency(summary.total_ac)}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900">
                  Estimates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-600">EAC (Estimate at Completion)</span>
                  <span className="text-sm font-medium text-slate-900">{formatCurrency(summary.eac)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-600">ETC (Estimate to Complete)</span>
                  <span className="text-sm font-medium text-slate-900">{formatCurrency(summary.etc)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-600">VAC (Variance at Completion)</span>
                  <span className={`text-sm font-medium ${(summary.vac || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(summary.vac)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-600">Overall Progress</span>
                  <span className="text-sm font-medium text-slate-900">{summary.percent_complete}%</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Activity EVM Table */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-slate-900">
                  Activity EVM Values
                </CardTitle>
                <Badge variant="outline" className="text-[10px]">
                  {summary.activities_with_bac}/{summary.total_activities} with BAC
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Activity</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">BAC</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">% Comp</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">PV</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">EV</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">AC</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">CV</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">CPI</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {evmData.activities.map((act) => (
                    <TableRow key={act.activity_id} className="hover:bg-slate-50/50">
                      <TableCell>
                        <div>
                          <span className="font-mono text-xs text-slate-500">{act.activity_code}</span>
                          <span className="ml-2 text-sm text-slate-900">{act.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{formatCurrency(act.bac)}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{act.percent_complete}%</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{formatCurrency(act.pv)}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{formatCurrency(act.ev)}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{formatCurrency(act.ac)}</TableCell>
                      <TableCell className={`text-right text-sm tabular-nums font-medium ${act.cv >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(act.cv)}
                      </TableCell>
                      <TableCell className={`text-right text-sm tabular-nums font-medium ${
                        act.cpi === null ? 'text-slate-400' :
                        act.cpi >= 1 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatIndex(act.cpi)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>

          {/* Info Note */}
          <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg text-slate-600">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-medium">EVM Formulas</p>
              <p className="mt-1">
                CPI = EV / AC (Cost Performance) | SPI = EV / PV (Schedule Performance) | 
                CV = EV - AC | SV = EV - PV | EAC = BAC / CPI
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <BarChart3 className="h-12 w-12 text-slate-300 mb-4" />
          <p className="text-sm text-slate-500">No EVM data available</p>
          <p className="text-xs text-slate-400 mt-1">Setup BAC values to start tracking</p>
        </div>
      )}

      {/* Setup BAC Dialog */}
      <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Setup Budget at Completion (BAC)</DialogTitle>
            <DialogDescription>
              Set a default BAC value for all activities without budget. 
              You can adjust individual activity BAC values later.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="default-bac">Default BAC per Activity ($)</Label>
              <Input
                id="default-bac"
                type="number"
                min="0"
                step="100"
                value={defaultBac}
                onChange={(e) => setDefaultBac(parseInt(e.target.value) || 0)}
                className="h-9"
              />
              <p className="text-xs text-slate-500">
                This will be applied to {summary?.total_activities - summary?.activities_with_bac || 'all'} activities without BAC.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setSetupDialogOpen(false)}
              disabled={settingUp}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSetupBac}
              disabled={settingUp || defaultBac <= 0}
              className="gap-2"
            >
              {settingUp && <Loader2 className="h-4 w-4 animate-spin" />}
              Apply Default BAC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EVMPage;
